import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const errorResponseSchema = z.object({
  error: z.object({ message: z.string(), code: z.string().optional() }),
});

export const plannerJobSchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  description: z.string().nullable(),
  address: z.string().nullable(),
  town: z.string().nullable(),
  pincode: z.string().nullable(),
  priority: z.string().nullable(),
  priority_name: z.string().nullable(),
  job_category_name: z.string().nullable(),
  client_name: z.string().nullable(),
  external_id: z.string().nullable(),
  req_completion_date: z.string().nullable(),
  start_date: z.string().nullable(),
  created_time: z.string().nullable(),
  updated_time: z.string().nullable(),
  work_order_status: z.string().nullable(),
  permit_required: z.boolean(),
  reinstatement_required: z.boolean(),
  permit_status_id: z.number().nullable(),
  permit_status_name: z.string().nullable(),
  duration: z.number().nullable(),
  coordinates: z.string().nullable().optional(),
  scheduled_date: z.string().nullable().optional(),
  scheduled_end_date: z.string().nullable().optional(),
  schedule_start: z.string().nullable().optional(),
  schedule_end: z.string().nullable().optional(),
  team_id: z.number().nullable().optional(),
  team_name: z.string().nullable().optional(),
  assigned_to: z.number().nullable().optional(),
  assigned_to_name: z.string().nullable().optional(),
});

export const unscheduledJobSchema = plannerJobSchema;

export const unscheduledJobsResponseSchema = z.object({
  data: z.array(unscheduledJobSchema),
  total: z.number(),
});

export type PlannerJob = z.infer<typeof plannerJobSchema>;
export type UnscheduledJob = z.infer<typeof unscheduledJobSchema>;
export type UnscheduledJobsResponse = z.infer<typeof unscheduledJobsResponseSchema>;

export const scheduledJobsResponseSchema = z.object({
  data: z.array(plannerJobSchema),
  total: z.number(),
});

export type ScheduledJobsResponse = z.infer<typeof scheduledJobsResponseSchema>;

/** Area lookup option for the Schedule page's Area filter (SC-6251). */
export const plannerAreaSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export type PlannerArea = z.infer<typeof plannerAreaSchema>;

export const unscheduledJobsContract = c.router({
  getPlannerAreas: {
    method: 'GET',
    path: '/areas',
    responses: {
      200: z.array(plannerAreaSchema),
      500: errorResponseSchema,
    },
    summary: 'Get active areas as options for the Schedule page Area filter',
  },
  getUnscheduledJobs: {
    method: 'GET',
    path: '/unscheduled-jobs',
    query: z.object({
      limit: z.coerce.number().optional().default(100),
      skip: z.coerce.number().optional().default(0),
      search: z.string().optional(),
      priority_id: z.string().optional(),
      job_category_id: z.string().optional(),
      area_id: z.string().optional(),
    }),
    responses: {
      200: unscheduledJobsResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get work orders in Ready for Planning status (unscheduled)',
  },
  getScheduledJobs: {
    method: 'GET',
    path: '/scheduled-jobs',
    query: z.object({
      limit: z.coerce.number().optional().default(200),
      skip: z.coerce.number().optional().default(0),
      search: z.string().optional(),
      priority_id: z.string().optional(),
      job_category_id: z.string().optional(),
      area_id: z.string().optional(),
    }),
    responses: {
      200: scheduledJobsResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get work orders in Planned status (scheduled) with their schedule data',
  },
});
